// © 2026 SoulMD, LLC. All rights reserved.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PatientWellnessPanel from './PatientWellnessPanel';
import MonthlyReviewDraft from './MonthlyReviewDraft';

interface Props { API: string; token: string; accent: string; }

interface Patient {
  id: number;
  name: string;
  email: string;
  dob: string | null;
  phone: string | null;
  membership_tier: 'awaken' | 'align' | 'ascend' | string;
  intake_data: IntakeData;
  doctor_notes: string;
  last_contact_at: string | null;
  is_approved: boolean;
  approved_at: string | null;
  age_verified?: boolean;       // 18+ self-attestation captured during onboarding intake
  // 3-month-trial → annual lifecycle (optional — older payloads
  // without these fields still render with no lifecycle badge).
  membership_status?: string;
  monthly_payment_count?: number;
  is_first_year?: boolean;
  remaining_balance_due_at?: string | null;
  annual_renewal_due_at?: string | null;
  grace_period_end?: string | null;
  downgraded_at?: string | null;
  created_at: string;
  updated_at: string;
}

// Per-member lifecycle pill. Single source-of-truth for the colour
// + label combinations the Members card and the Insights tab share.
const LIFECYCLE_BADGE: Record<string, { label: string; bg: string; fg: string }> = {
  active_monthly:        { label: 'Monthly',         bg: 'rgba(83,74,183,0.10)',  fg: '#534AB7' },
  balance_invoice_sent:  { label: 'Balance Due',     bg: 'rgba(232,168,64,0.18)', fg: '#8a5a10' },
  grace_period:          { label: 'Grace',           bg: 'rgba(232,118,40,0.18)', fg: '#8a3a10' },
  active_annual:         { label: 'Annual ✓',        bg: 'rgba(46,140,90,0.15)',  fg: '#2e8c5a' },
  renewal_invoice_sent:  { label: 'Renewal Due',     bg: 'rgba(232,118,40,0.18)', fg: '#8a3a10' },
  renewal_grace_period:  { label: 'Renewal Grace',   bg: 'rgba(232,118,40,0.18)', fg: '#8a3a10' },
  downgraded_alacarte:   { label: 'À La Carte',      bg: 'rgba(107,78,124,0.15)', fg: '#6B4E7C' },
};
const lifecycleLabel = (p: Patient): string | null => {
  const ms = p.membership_status;
  if (!ms) return null;
  if (ms === 'active_monthly') {
    const n = p.monthly_payment_count ?? 0;
    return `Monthly ${Math.min(n, 3)}/3`;
  }
  return LIFECYCLE_BADGE[ms]?.label || null;
};
const lifecycleColors = (p: Patient): { bg: string; fg: string } => {
  const ms = p.membership_status || 'active_monthly';
  return LIFECYCLE_BADGE[ms] || LIFECYCLE_BADGE.active_monthly;
};

interface IntakeData {
  chief_complaint?: string;
  medical_history?: string;
  medications?: string;
  allergies?: string;
  goals_medical?: string;
  goals_coaching?: string;
  goals_spiritual?: string;
  communication_preference?: string;
}

const TIERS: {id: string; label: string; price: string; color: string}[] = [
  { id: 'awaken', label: 'Awaken', price: '$150/mo', color: '#7ab0f0' },
  { id: 'align',  label: 'Align',  price: '$300/mo', color: '#4a7ad0' },
  { id: 'ascend', label: 'Ascend', price: '$500/mo', color: '#1a2a4a' },
];

const CARD: React.CSSProperties = {
  background: 'rgba(255,255,255,0.85)', borderRadius:'16px',
  border: '1px solid rgba(122,176,240,0.2)',
  boxShadow: '0 2px 10px rgba(100,130,200,0.1)',
  padding:'16px',
};

const INPUT: React.CSSProperties = {
  width:'100%', padding:'10px 12px', borderRadius:'10px',
  border:'1px solid rgba(122,176,240,0.3)',
  fontSize:'13px', color:'#1a2a4a', background:'rgba(240,246,255,0.5)',
  outline:'none', boxSizing:'border-box',
};

const FIELD_LABEL: React.CSSProperties = { fontSize:'11px', color:'#4a7ad0', fontWeight:600, marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.5px' };

const PatientsSection: React.FC<Props> = ({ API, token, accent }) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Patient | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [actingId, setActingId] = useState<number | null>(null);
  const [actingMessage, setActingMessage] = useState<string>('');
  const [revokeTarget, setRevokeTarget] = useState<Patient | null>(null);
  const [declineTarget, setDeclineTarget] = useState<Patient | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/concierge/patients`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { setPatients(data.patients || []); })
      .catch(() => setError('Could not load patients.'))
      .finally(() => setLoading(false));
  }, [API, token]);

  useEffect(() => { load(); }, [load]);

  const { pending, active } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (p: Patient) => !q || (
      p.name.toLowerCase().includes(q) ||
      p.email.toLowerCase().includes(q) ||
      (p.phone || '').toLowerCase().includes(q)
    );
    return {
      pending: patients.filter(p => !p.is_approved && matches(p)),
      active:  patients.filter(p =>  p.is_approved && matches(p)),
    };
  }, [patients, search]);

  const flash = (msg: string) => {
    setActingMessage(msg);
    setTimeout(() => setActingMessage(''), 2500);
  };

  const approve = async (p: Patient) => {
    setActingId(p.id);
    try {
      const res = await fetch(`${API}/concierge/patients/${p.id}/approve`, {
        method:'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).detail || 'Approve failed');
      flash(`✓ Approved ${p.name || p.email} — welcome email sent.`);
      load();
    } catch (e: any) {
      alert(e.message || 'Approve failed');
    } finally {
      setActingId(null);
    }
  };

  const revoke = async (p: Patient) => {
    setActingId(p.id);
    try {
      const res = await fetch(`${API}/concierge/patients/${p.id}/revoke`, {
        method:'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).detail || 'Revoke failed');
      flash(`Revoked ${p.name || p.email} — patient record retained.`);
      setRevokeTarget(null);
      load();
    } catch (e: any) {
      alert(e.message || 'Revoke failed');
    } finally {
      setActingId(null);
    }
  };

  const decline = async (p: Patient) => {
    setActingId(p.id);
    try {
      const res = await fetch(`${API}/concierge/patients/${p.id}`, {
        method:'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).detail || 'Decline failed');
      flash(`Declined ${p.name || p.email}.`);
      setDeclineTarget(null);
      load();
    } catch (e: any) {
      alert(e.message || 'Decline failed');
    } finally {
      setActingId(null);
    }
  };

  if (selected) {
    return <PatientDetail API={API} token={token} accent={accent} patient={selected} onClose={()=>{setSelected(null); load();}} onDeleted={()=>{setSelected(null); load();}}/>;
  }

  return (
    <div>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:'12px', flexWrap:'wrap', marginBottom:'14px'}}>
        <div>
          <div style={{fontSize:'20px', fontWeight:800, color:'#1a2a4a'}}>Members</div>
          <div style={{fontSize:'12px', color:'#4a7ad0'}}>
            {pending.length} pending · {active.length} active
            {active.length > 0 && (<>
              {' '}· {active.filter(p=>p.membership_tier==='ascend').length} Ascend · {active.filter(p=>p.membership_tier==='align').length} Align · {active.filter(p=>p.membership_tier==='awaken').length} Awaken
            </>)}
          </div>
        </div>
        <button onClick={()=>setShowAdd(true)} style={{background:accent, border:'none', borderRadius:'12px', padding:'10px 18px', fontSize:'13px', fontWeight:700, color:'white', cursor:'pointer', whiteSpace:'nowrap'}}>+ Add patient</button>
      </div>

      <input
        type="search"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search name, email, or phone…"
        style={{...INPUT, marginBottom:'14px'}}
      />

      {error && <div style={{background:'rgba(224,80,80,0.1)', border:'1px solid rgba(224,80,80,0.3)', borderRadius:'10px', padding:'10px 12px', color:'#a02020', fontSize:'12px', marginBottom:'12px'}}>{error}</div>}
      {actingMessage && <div style={{background:'rgba(201,168,76,0.10)', border:'1px solid rgba(201,168,76,0.4)', borderRadius:'10px', padding:'10px 14px', color:'#7a5a10', fontSize:'12.5px', marginBottom:'12px', fontWeight:700}}>{actingMessage}</div>}

      {loading ? (
        <div style={{padding:'40px', textAlign:'center', color:'#4a7ad0', fontSize:'13px'}}>Loading patients…</div>
      ) : (
        <>
          {/* ───── Pending Requests ───── */}
          <SectionHeader
            label="Pending requests"
            count={pending.length}
            note="Approve to send welcome magic link · Decline to remove the request entirely."
          />
          {pending.length === 0 ? (
            <div style={{...CARD, textAlign:'center', padding:'18px', color:'#7090a8', fontSize:'12.5px', fontStyle:'italic', marginBottom:'24px'}}>
              No pending requests right now.
            </div>
          ) : (
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:'12px', marginBottom:'24px'}}>
              {pending.map(p => {
                const requested = new Date(p.created_at);
                const busy = actingId === p.id;
                return (
                  <div key={p.id} style={{...CARD, borderColor:'rgba(201,168,76,0.45)', background:'linear-gradient(180deg, rgba(255,250,236,0.85), rgba(255,255,255,0.85))'}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'8px', marginBottom:'6px'}}>
                      <div style={{fontSize:'14px', fontWeight:800, color:'#1a2a4a', minWidth:0, wordBreak:'break-word'}}>{p.name || <span style={{color:'#A88830', fontStyle:'italic', fontWeight:600}}>(no name)</span>}</div>
                      <span style={{fontSize:'10px', padding:'3px 8px', borderRadius:'999px', background:'rgba(201,168,76,0.15)', color:'#7a5a10', fontWeight:800, letterSpacing:'0.6px', textTransform:'uppercase', whiteSpace:'nowrap'}}>Pending</span>
                    </div>
                    <div style={{fontSize:'12px', color:'#4a5e6a', marginBottom:'10px', wordBreak:'break-all'}}>{p.email}</div>
                    <div style={{fontSize:'11px', color:'#7090a8', marginBottom:'12px'}}>Requested {requested.toLocaleString()}</div>
                    <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                      <button
                        disabled={busy}
                        onClick={()=>approve(p)}
                        style={{
                          background:'#C9A84C', border:'none', color:'white',
                          borderRadius:'10px', padding:'8px 16px',
                          fontSize:'12.5px', fontWeight:800, letterSpacing:'0.4px',
                          cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1,
                          fontFamily:'inherit', boxShadow:'0 4px 12px rgba(201,168,76,0.28)',
                        }}>
                        {busy ? 'Approving…' : 'Approve'}
                      </button>
                      <button
                        disabled={busy}
                        onClick={()=>setDeclineTarget(p)}
                        style={{
                          background:'transparent', border:'1px solid rgba(192,64,64,0.4)',
                          color:'#c04040', borderRadius:'10px', padding:'8px 14px',
                          fontSize:'12.5px', fontWeight:700, cursor: busy ? 'wait' : 'pointer',
                          fontFamily:'inherit',
                        }}>
                        Decline
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ───── Active Members ───── */}
          <SectionHeader
            label="Active members"
            count={active.length}
            note="Click a member to open their full record."
          />
          {active.length === 0 ? (
            <div style={{...CARD, textAlign:'center', padding:'40px 20px', color:'#4a7ad0'}}>
              <div style={{fontSize:'36px', marginBottom:'10px', opacity:0.4}}>👥</div>
              <div style={{fontSize:'14px', fontWeight:700, color:'#1a2a4a', marginBottom:'4px'}}>{patients.length === 0 ? 'No members yet' : 'No active members match your search'}</div>
              <div style={{fontSize:'12px'}}>{patients.length === 0 ? 'Add a patient and approve to begin.' : 'Try a different search.'}</div>
            </div>
          ) : (
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:'12px'}}>
              {active.map(p => {
                const tier = TIERS.find(t => t.id === p.membership_tier) || TIERS[0];
                const last = p.last_contact_at ? new Date(p.last_contact_at) : null;
                const approved = p.approved_at ? new Date(p.approved_at) : null;
                const busy = actingId === p.id;
                return (
                  <div key={p.id} style={{...CARD}}>
                    <button
                      onClick={()=>setSelected(p)}
                      style={{background:'transparent', border:'none', padding:0, textAlign:'left', cursor:'pointer', fontFamily:'inherit', width:'100%'}}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'8px', gap:'8px'}}>
                        <div style={{fontSize:'15px', fontWeight:800, color:'#1a2a4a', minWidth:0, wordBreak:'break-word'}}>{p.name}</div>
                        <span style={{fontSize:'10px', padding:'3px 8px', borderRadius:'999px', background:`${tier.color}1a`, color:tier.color, fontWeight:700, letterSpacing:'0.5px', textTransform:'uppercase', whiteSpace:'nowrap'}}>{tier.label}</span>
                      </div>
                      <div style={{fontSize:'12px', color:'#4a5e6a', marginBottom:'8px', wordBreak:'break-all'}}>{p.email}</div>
                      <div style={{display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center', marginBottom:'4px'}}>
                        {(() => {
                          const label = lifecycleLabel(p);
                          if (!label) return null;
                          const c = lifecycleColors(p);
                          return (
                            <span title={`Lifecycle: ${p.membership_status}`} style={{
                              fontSize:'10px', padding:'2px 8px', borderRadius:'999px',
                              background: c.bg, color: c.fg,
                              fontWeight:700, letterSpacing:'0.4px', textTransform:'uppercase',
                            }}>{label}</span>
                          );
                        })()}
                        {p.age_verified ? (
                          <span title="Patient confirmed 18+ during onboarding intake" style={{
                            fontSize:'10px', padding:'2px 8px', borderRadius:'999px',
                            background:'rgba(46,140,90,0.10)', color:'#2e8c5a',
                            fontWeight:700, letterSpacing:'0.4px', textTransform:'uppercase',
                          }}>18+ ✓</span>
                        ) : (
                          <span title="Age verification pending — fires on first intake submission" style={{
                            fontSize:'10px', padding:'2px 8px', borderRadius:'999px',
                            background:'rgba(160,128,40,0.12)', color:'#7a5e10',
                            fontWeight:700, letterSpacing:'0.4px', textTransform:'uppercase',
                          }}>Age unverified</span>
                        )}
                      </div>
                      <div style={{display:'flex', gap:'10px', flexWrap:'wrap', fontSize:'11px', color:'#4a7ad0'}}>
                        {approved && <span>Approved {approved.toLocaleDateString()}</span>}
                        {last && <span>· Last contact {last.toLocaleDateString()}</span>}
                      </div>
                    </button>
                    <button
                      disabled={busy}
                      onClick={(e)=>{e.stopPropagation(); setRevokeTarget(p);}}
                      style={{
                        marginTop:'10px',
                        background:'transparent', border:'1px solid rgba(112,144,168,0.35)',
                        color:'#7090a8', borderRadius:'8px', padding:'6px 12px',
                        fontSize:'11.5px', fontWeight:700,
                        cursor: busy ? 'wait' : 'pointer', fontFamily:'inherit',
                      }}>
                      Revoke access
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {showAdd && <AddPatientModal API={API} token={token} accent={accent} onClose={()=>setShowAdd(false)} onCreated={()=>{setShowAdd(false); load();}}/>}

      {revokeTarget && (
        <ConfirmModal
          title="Revoke patient access?"
          body={<>This will block <b>{revokeTarget.name || revokeTarget.email}</b> from signing in to the patient portal. Their clinical record (messages, intake, notes, billing) is preserved and can be re-approved later.</>}
          confirmLabel={actingId === revokeTarget.id ? 'Revoking…' : 'Revoke access'}
          confirmStyle={{background:'#7090a8'}}
          onCancel={()=>setRevokeTarget(null)}
          onConfirm={()=>revoke(revokeTarget)}
          busy={actingId === revokeTarget.id}
        />
      )}

      {declineTarget && (
        <ConfirmModal
          title="Decline this request?"
          body={<>This permanently removes the request from <b>{declineTarget.email}</b>. They will not be notified.</>}
          confirmLabel={actingId === declineTarget.id ? 'Declining…' : 'Decline & remove'}
          confirmStyle={{background:'#c04040'}}
          onCancel={()=>setDeclineTarget(null)}
          onConfirm={()=>decline(declineTarget)}
          busy={actingId === declineTarget.id}
        />
      )}
    </div>
  );
};

const SectionHeader: React.FC<{label:string; count:number; note?:string}> = ({ label, count, note }) => (
  <div style={{margin:'4px 0 10px'}}>
    <div style={{display:'flex', alignItems:'baseline', gap:'10px'}}>
      <div style={{fontSize:'13px', fontWeight:800, color:'#1a2a4a', letterSpacing:'0.6px', textTransform:'uppercase'}}>{label}</div>
      <div style={{fontSize:'11px', color:'#7090a8', fontWeight:700}}>· {count}</div>
    </div>
    {note && <div style={{fontSize:'11.5px', color:'#7090a8', marginTop:'2px'}}>{note}</div>}
  </div>
);

const ConfirmModal: React.FC<{
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  confirmStyle?: React.CSSProperties;
  onCancel: ()=>void;
  onConfirm: ()=>void;
  busy?: boolean;
}> = ({ title, body, confirmLabel, confirmStyle, onCancel, onConfirm, busy }) => (
  <div style={{position:'fixed', inset:0, background:'rgba(26,42,74,0.45)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px', zIndex:1500}}>
    <div style={{background:'white', borderRadius:'18px', padding:'24px', maxWidth:'420px', width:'100%', boxShadow:'0 16px 50px rgba(26,42,74,0.3)'}}>
      <div style={{fontSize:'16px', fontWeight:800, color:'#1a2a4a', marginBottom:'8px'}}>{title}</div>
      <div style={{fontSize:'13px', color:'#4a5e6a', marginBottom:'18px', lineHeight:1.6}}>{body}</div>
      <div style={{display:'flex', gap:'10px', justifyContent:'flex-end'}}>
        <button onClick={onCancel} disabled={busy} style={{background:'rgba(255,255,255,0.85)', border:'1px solid rgba(122,176,240,0.3)', borderRadius:'10px', padding:'9px 16px', fontSize:'13px', fontWeight:600, color:'#4a7ad0', cursor:'pointer'}}>Cancel</button>
        <button onClick={onConfirm} disabled={busy} style={{border:'none', borderRadius:'10px', padding:'9px 16px', fontSize:'13px', fontWeight:700, color:'white', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1, ...confirmStyle}}>{confirmLabel}</button>
      </div>
    </div>
  </div>
);

// ───── Add Patient Modal ─────────────────────────────────────────────────────

const AddPatientModal: React.FC<{API:string; token:string; accent:string; onClose:()=>void; onCreated:()=>void}> = ({API, token, accent, onClose, onCreated}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [phone, setPhone] = useState('');
  const [tier, setTier] = useState<string>('awaken');
  const [intake, setIntake] = useState<IntakeData>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const update = (k: keyof IntakeData, v: string) => setIntake(x => ({...x, [k]: v}));

  const save = async () => {
    setError('');
    if (!name.trim() || !email.trim()) { setError('Name and email required.'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/concierge/patients`, {
        method:'POST',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${token}`},
        body: JSON.stringify({ name, email, dob: dob || undefined, phone: phone || undefined, membership_tier: tier, intake_data: intake }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not save patient');
      onCreated();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{position:'fixed', inset:0, background:'rgba(26,42,74,0.45)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'20px', overflowY:'auto', zIndex:1500}}>
      <div style={{background:'white', borderRadius:'20px', padding:'24px', maxWidth:'640px', width:'100%', boxShadow:'0 20px 60px rgba(26,42,74,0.3)', margin:'20px 0'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px'}}>
          <div>
            <div style={{fontSize:'18px', fontWeight:800, color:'#1a2a4a'}}>Add patient</div>
            <div style={{fontSize:'12px', color:'#4a7ad0'}}>Core info + optional intake form. You can fill intake later.</div>
          </div>
          <button onClick={onClose} style={{background:'transparent', border:'none', fontSize:'20px', color:'#4a7ad0', cursor:'pointer'}}>×</button>
        </div>

        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:'12px', marginBottom:'14px'}}>
          <div>
            <div style={FIELD_LABEL}>Name *</div>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder="Full name" style={INPUT}/>
          </div>
          <div>
            <div style={FIELD_LABEL}>Email *</div>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="patient@example.com" style={INPUT}/>
          </div>
          <div>
            <div style={FIELD_LABEL}>Date of birth</div>
            <input type="date" value={dob} onChange={e=>setDob(e.target.value)} style={INPUT}/>
          </div>
          <div>
            <div style={FIELD_LABEL}>Phone</div>
            <input type="tel" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="+1 ..." style={INPUT}/>
          </div>
          <div>
            <div style={FIELD_LABEL}>Membership tier</div>
            <select value={tier} onChange={e=>setTier(e.target.value)} style={{...INPUT, appearance:'auto'}}>
              {TIERS.map(t => <option key={t.id} value={t.id}>{t.label} — {t.price}</option>)}
            </select>
          </div>
        </div>

        <div style={{borderTop:'1px solid rgba(122,176,240,0.2)', paddingTop:'16px', marginTop:'6px'}}>
          <div style={{fontSize:'12px', fontWeight:700, color:'#1a2a4a', marginBottom:'10px', letterSpacing:'0.5px', textTransform:'uppercase'}}>Intake form (optional)</div>
          <div style={{display:'grid', gridTemplateColumns:'1fr', gap:'10px'}}>
            <TextArea label="Chief complaint / reason for joining" value={intake.chief_complaint||''} onChange={v=>update('chief_complaint', v)}/>
            <TextArea label="Medical history (conditions, surgeries)" value={intake.medical_history||''} onChange={v=>update('medical_history', v)}/>
            <TextArea label="Current medications" value={intake.medications||''} onChange={v=>update('medications', v)}/>
            <TextArea label="Allergies" value={intake.allergies||''} onChange={v=>update('allergies', v)}/>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:'10px'}}>
              <TextArea label="Medical goals" value={intake.goals_medical||''} onChange={v=>update('goals_medical', v)} small/>
              <TextArea label="Life coaching goals" value={intake.goals_coaching||''} onChange={v=>update('goals_coaching', v)} small/>
              <TextArea label="Spiritual goals" value={intake.goals_spiritual||''} onChange={v=>update('goals_spiritual', v)} small/>
            </div>
            <div>
              <div style={FIELD_LABEL}>Preferred communication</div>
              <select value={intake.communication_preference || ''} onChange={e=>update('communication_preference', e.target.value)} style={{...INPUT, appearance:'auto'}}>
                <option value="">(not set)</option>
                <option value="email">Email</option>
                <option value="sms">Text / SMS</option>
                <option value="phone">Phone</option>
                <option value="in_person">In person only</option>
                <option value="telehealth">Telehealth video</option>
              </select>
            </div>
          </div>
        </div>

        {error && <div style={{background:'rgba(224,80,80,0.1)', border:'1px solid rgba(224,80,80,0.3)', borderRadius:'10px', padding:'10px 12px', color:'#a02020', fontSize:'12px', marginTop:'12px'}}>{error}</div>}

        <div style={{display:'flex', gap:'10px', justifyContent:'flex-end', marginTop:'16px'}}>
          <button onClick={onClose} disabled={saving} style={{background:'rgba(255,255,255,0.85)', border:'1px solid rgba(122,176,240,0.3)', borderRadius:'12px', padding:'10px 18px', fontSize:'13px', fontWeight:600, color:'#4a7ad0', cursor:'pointer'}}>Cancel</button>
          <button onClick={save} disabled={saving || !name.trim() || !email.trim()} style={{background:accent, border:'none', borderRadius:'12px', padding:'10px 20px', fontSize:'13px', fontWeight:700, color:'white', cursor:'pointer', opacity:(saving||!name.trim()||!email.trim())?0.6:1}}>
            {saving ? 'Saving…' : 'Save patient'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TextArea: React.FC<{label:string; value:string; onChange:(v:string)=>void; small?:boolean}> = ({label, value, onChange, small}) => (
  <div>
    <div style={FIELD_LABEL}>{label}</div>
    <textarea value={value} onChange={e=>onChange(e.target.value)} style={{...INPUT, minHeight: small ? '60px' : '70px', resize:'vertical', fontFamily:'inherit'}}/>
  </div>
);

// ───── Patient Detail ──────────────────────────────────────────────────────

const PatientDetail: React.FC<{API:string; token:string; accent:string; patient:Patient; onClose:()=>void; onDeleted:()=>void}> = ({API, token, accent, patient, onClose, onDeleted}) => {
  const [p, setP] = useState<Patient>(patient);
  const [doctorNotes, setDoctorNotes] = useState(patient.doctor_notes);
  const [intake, setIntake] = useState<IntakeData>(patient.intake_data || {});
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  // Slide-in panel for the AI-drafted monthly wellness review.
  const [draftReviewOpen, setDraftReviewOpen] = useState(false);

  const update = (k: keyof IntakeData, v: string) => setIntake(x => ({...x, [k]: v}));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/concierge/patients/${p.id}`, {
        method:'PATCH',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${token}`},
        body: JSON.stringify({ doctor_notes: doctorNotes, intake_data: intake }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      const data = await res.json();
      setP(data); setIntake(data.intake_data || {}); setDoctorNotes(data.doctor_notes || '');
      setSavedTick(true); setTimeout(()=>setSavedTick(false), 1500);
      setEditMode(false);
    } catch (e: any) { alert(e.message); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await fetch(`${API}/concierge/patients/${p.id}`, { method:'DELETE', headers:{Authorization:`Bearer ${token}`} });
      onDeleted();
    } catch {}
  };

  const tier = TIERS.find(t => t.id === p.membership_tier) || TIERS[0];
  const age = p.dob ? Math.floor((Date.now() - new Date(p.dob).getTime()) / 31557600000) : null;

  return (
    <div>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:'10px', flexWrap:'wrap', marginBottom:'16px'}}>
        <button onClick={onClose} style={{background:'transparent', border:'1px solid rgba(122,176,240,0.3)', borderRadius:'10px', padding:'7px 14px', fontSize:'12px', fontWeight:600, color:'#4a7ad0', cursor:'pointer'}}>← All patients</button>
        <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
          <button
            onClick={() => setDraftReviewOpen(true)}
            title="AI-drafted monthly wellness review"
            style={{
              background:'#C9A84C', border:'none', borderRadius:'10px',
              padding:'7px 16px', fontSize:'12.5px', fontWeight:800,
              color:'white', cursor:'pointer',
              fontFamily:'Georgia, "Cormorant Garamond", "Playfair Display", serif',
              letterSpacing:'0.4px',
              boxShadow:'0 6px 14px rgba(201,168,76,0.30)',
            }}>
            ✦ Draft Monthly Review
          </button>
          {!editMode && <button onClick={()=>setEditMode(true)} style={{background:'rgba(255,255,255,0.9)', border:'1px solid rgba(122,176,240,0.4)', borderRadius:'10px', padding:'7px 14px', fontSize:'12px', fontWeight:700, color:'#4a7ad0', cursor:'pointer'}}>Edit intake</button>}
          {editMode && <button onClick={save} disabled={saving} style={{background:accent, border:'none', borderRadius:'10px', padding:'7px 14px', fontSize:'12px', fontWeight:700, color:'white', cursor:'pointer', opacity:saving?0.6:1}}>{saving ? 'Saving…' : savedTick ? '✓ Saved' : 'Save changes'}</button>}
          <button onClick={()=>setDeleteConfirm(true)} style={{background:'transparent', border:'1px solid rgba(192,64,64,0.35)', borderRadius:'10px', padding:'7px 12px', fontSize:'12px', fontWeight:600, color:'#c04040', cursor:'pointer'}}>Delete</button>
        </div>
      </div>

      {/* Header card */}
      <div style={{...CARD, marginBottom:'14px'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'12px', flexWrap:'wrap'}}>
          <div style={{minWidth:0, flex:1}}>
            <div style={{fontSize:'22px', fontWeight:800, color:'#1a2a4a', marginBottom:'4px'}}>{p.name}</div>
            <div style={{fontSize:'13px', color:'#4a5e6a', wordBreak:'break-all'}}>{p.email}</div>
            <div style={{display:'flex', gap:'12px', marginTop:'8px', flexWrap:'wrap', fontSize:'12px', color:'#4a7ad0'}}>
              {age !== null && <span>Age {age}</span>}
              {p.dob && <span>· DOB {p.dob}</span>}
              {p.phone && <span>· {p.phone}</span>}
            </div>
          </div>
          <span style={{fontSize:'11px', padding:'5px 12px', borderRadius:'999px', background:`${tier.color}1a`, color:tier.color, fontWeight:700, letterSpacing:'0.5px', textTransform:'uppercase', whiteSpace:'nowrap'}}>{tier.label} · {tier.price}</span>
        </div>
      </div>

      {/* Two-column on desktop, stacked on mobile */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))', gap:'14px'}}>

        {/* Intake form */}
        <div style={{...CARD}}>
          <div style={{fontSize:'13px', fontWeight:800, color:'#1a2a4a', marginBottom:'12px', letterSpacing:'0.5px', textTransform:'uppercase'}}>Intake</div>
          {[
            ['chief_complaint', 'Chief complaint / reason for joining'],
            ['medical_history', 'Medical history'],
            ['medications', 'Current medications'],
            ['allergies', 'Allergies'],
            ['goals_medical', 'Medical goals'],
            ['goals_coaching', 'Life coaching goals'],
            ['goals_spiritual', 'Spiritual goals'],
            ['communication_preference', 'Preferred communication'],
          ].map(([k, label]) => (
            <div key={k} style={{marginBottom:'12px'}}>
              <div style={FIELD_LABEL}>{label}</div>
              {editMode ? (
                <textarea value={(intake as any)[k] || ''} onChange={e => update(k as keyof IntakeData, e.target.value)} style={{...INPUT, minHeight:'60px', resize:'vertical', fontFamily:'inherit'}}/>
              ) : (
                <div style={{fontSize:'13px', color:'#1a2a4a', lineHeight:1.6, whiteSpace:'pre-wrap', minHeight:'20px'}}>
                  {(intake as any)[k] || <span style={{color:'#7ab0f0', fontStyle:'italic'}}>(not filled)</span>}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Doctor notes + metadata */}
        <div style={{display:'flex', flexDirection:'column', gap:'14px'}}>
          <div style={{...CARD, background:'rgba(240,246,255,0.6)', border:'1px solid rgba(122,176,240,0.4)'}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px'}}>
              <div style={{fontSize:'13px', fontWeight:800, color:'#1a2a4a', letterSpacing:'0.5px', textTransform:'uppercase'}}>Doctor notes</div>
              <div style={{fontSize:'10px', color:'#4a7ad0', fontStyle:'italic'}}>Private — only visible here</div>
            </div>
            <textarea
              value={doctorNotes}
              onChange={e => setDoctorNotes(e.target.value)}
              placeholder="Your private notes on this patient. Medical observations, coaching progress, anything to remember."
              style={{...INPUT, minHeight:'240px', resize:'vertical', fontFamily:'inherit', lineHeight:1.6, background:'rgba(240,246,255,0.6)'}}
            />
            <button onClick={save} disabled={saving} style={{marginTop:'10px', background:accent, border:'none', borderRadius:'10px', padding:'8px 16px', fontSize:'12px', fontWeight:700, color:'white', cursor:'pointer', opacity:saving?0.6:1}}>
              {saving ? 'Saving…' : savedTick ? '✓ Saved' : 'Save notes'}
            </button>
          </div>

          <div style={{...CARD}}>
            <div style={{fontSize:'13px', fontWeight:800, color:'#1a2a4a', marginBottom:'10px', letterSpacing:'0.5px', textTransform:'uppercase'}}>Record</div>
            <div style={{fontSize:'12px', color:'#4a5e6a', lineHeight:1.8}}>
              <div><b>Patient ID:</b> #{p.id}</div>
              <div><b>Joined:</b> {new Date(p.created_at).toLocaleString()}</div>
              <div><b>Last updated:</b> {new Date(p.updated_at).toLocaleString()}</div>
              {p.last_contact_at && <div><b>Last contact:</b> {new Date(p.last_contact_at).toLocaleString()}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Wellness panel — energy log trend + post-meditation reflections.
          Lives below the intake row so the doctor sees objective patient
          state (mood pattern, journal notes) without scrolling past the
          intake form she relies on at the start of each visit. */}
      <div style={{marginTop:'14px'}}>
        <PatientWellnessPanel API={API} token={token} patientId={p.id} accent={accent}/>
      </div>

      {deleteConfirm && (
        <div style={{position:'fixed', inset:0, background:'rgba(26,42,74,0.45)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px', zIndex:1500}}>
          <div style={{background:'white', borderRadius:'18px', padding:'24px', maxWidth:'420px', width:'100%', boxShadow:'0 16px 50px rgba(26,42,74,0.3)'}}>
            <div style={{fontSize:'16px', fontWeight:800, color:'#1a2a4a', marginBottom:'8px'}}>Delete patient?</div>
            <div style={{fontSize:'13px', color:'#4a5e6a', marginBottom:'18px', lineHeight:1.6}}>
              This will permanently delete <b>{p.name}</b>'s record and ALL associated messages, appointments, invoices, habits, and assignments. This cannot be undone.
            </div>
            <div style={{display:'flex', gap:'10px', justifyContent:'flex-end'}}>
              <button onClick={()=>setDeleteConfirm(false)} style={{background:'rgba(255,255,255,0.85)', border:'1px solid rgba(122,176,240,0.3)', borderRadius:'10px', padding:'9px 16px', fontSize:'13px', fontWeight:600, color:'#4a7ad0', cursor:'pointer'}}>Cancel</button>
              <button onClick={doDelete} style={{background:'#c04040', border:'none', borderRadius:'10px', padding:'9px 16px', fontSize:'13px', fontWeight:700, color:'white', cursor:'pointer'}}>Delete permanently</button>
            </div>
          </div>
        </div>
      )}

      {draftReviewOpen && (
        <MonthlyReviewDraft
          API={API} token={token}
          patientId={p.id}
          patientName={p.name}
          accent={accent}
          onClose={() => setDraftReviewOpen(false)}
        />
      )}
    </div>
  );
};

export default PatientsSection;
